// Client-side enforcement of the keyword operators an analyst typed into a job's search term
// (e.g. `"barbie" "doctor" "satisfying" -"hello kitty"`). The YouTube Data API treats `-term` as a
// negative *ranking* signal, not a hard NOT, so excluded content still comes back in the results -
// this re-applies the analyst's own operators as a strict filter over the items actually returned.
import {stemmer} from 'stemmer';
import type {Item} from '../api/items';
import {getMetaFields} from './itemMetadata';

export type SearchQuery = {include: string[]; exclude: string[]};
export type IncludeMode = 'all' | 'any';

export type MatchOptions = {
  /** How include terms combine. Ignored when `applyIncludes` is false. */
  readonly includeMode: IncludeMode;
  /**
   Whether include terms filter at all - off by default in the UI. YouTube already matched the
   query, often semantically and against a transcript and engagement signals we never see, so
   re-requiring the words to appear literally in the fields we do store can only discard items it
   considered relevant. Exclusions are always applied: they're the operator YouTube won't honour.
   */
  readonly applyIncludes: boolean;
  /** Match word variants ("doctors" ~ "doctor"), the way the server's `english` FTS config does. */
  readonly stem: boolean;
};

// Quoted phrases capture their leading `-` separately since it sits outside the quotes; a bare
// token captures whole (including any leading `-`) and gets split apart below - `-?` directly
// ahead of `\S+` would let the two groups disagree about which of them "owns" a leading dash.
const TOKEN_PATTERN = /(-?)"([^"]*)"|(\S+)/g;

// Unicode-aware, so a Cyrillic/Greek/Arabic/CJK term survives normalisation. `\p{N}` is a superset
// of `\d`; the previous `[^\da-z]` stripped every non-ASCII character, collapsing such terms to the
// empty string - which then matched either everything or nothing depending on the code path.
// Emoji are matchable content too: pictographs, skin tones, flag letters and the U+200D joiner that
// builds sequences like 👩‍💻. U+FE0F is a mark, so `fold` drops it from both sides alike.
const NON_ALPHANUMERIC =
  /[^\p{Emoji_Modifier}\p{Extended_Pictographic}\p{Letter}\p{Number}\p{Regional_Indicator}\u{200D}]+/gu;
const COMBINING_MARK = /\p{Mark}/gu;

// Scripts conventionally written without spaces between words. A word-boundary check can never
// match inside them - the whole run normalises to one token - so terms containing them fall back to
// squashed containment the way multi-word phrases do.
// Emoji count here too: they are glued to words and to each other ("fire🔥🔥", "👍🏽").
const SCRIPT_WITHOUT_WORD_BREAKS =
  /[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Script=Han}\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}]/u;

/**
 Lowercased and accent-folded, so `-"café"` also catches "cafe". Deliberately looser than the
 server, which has no `unaccent` extension available - the client is the more permissive layer, and
 folding the term and the text identically keeps the comparison self-consistent either way. (NFD
 also decomposes Hangul syllables into jamo, harmless here for the same reason.)
 */
function fold(text: string): string {
  return text.toLowerCase().normalize('NFD').replaceAll(COMBINING_MARK, '');
}

/**
 Non-alphanumeric runs collapsed to a single space and padded, so `.includes(' term ')` is a word
 boundary check - stops "cat" matching "catalogue", while still treating `#barbie` or
 `barbie,doctor` as containing "barbie". With `stem`, each word is reduced to its stem before
 rejoining; stemming per token rather than over the joined string is what keeps that boundary
 intact ("cat" stems to "cat", "catalogue" to "catalogu", so they still don't match).
 */
function toSpacedWords(text: string, stem: boolean): string {
  const collapsed = fold(text).replaceAll(NON_ALPHANUMERIC, ' ').trim();
  if (!collapsed) {
    // One space, never two: `' ' + '' + ' '` is a substring of every padded haystack.
    return ' ';
  }

  const words = collapsed.split(' ');
  return ` ${(stem ? words.map(stemmer) : words).join(' ')} `;
}

/**
 All non-alphanumeric characters removed, so `hello kitty` also catches concatenated metadata spam
 like `#hellokitty` or `hellokittykitchenset`. Never stemmed - it exists for glued-together spam,
 where word variants have no meaning.
 */
function toSquashed(text: string): string {
  return fold(text).replaceAll(NON_ALPHANUMERIC, '');
}

/**
 Squashed containment is deliberately not used for a single Latin-script word - "cat" inside
 "concatenate" over-matches badly. A multi-word phrase can't collide that way, and a term in a
 script without word breaks has no other way to match at all.
 */
function allowsSquashedMatch(term: string): boolean {
  return term.includes(' ') || SCRIPT_WITHOUT_WORD_BREAKS.test(term);
}

function termMatches(spaced: string, squashed: string, term: string, stem: boolean): boolean {
  const termSquashed = toSquashed(term);
  if (!termSquashed) {
    // Pure punctuation - no matchable content. Must not fall through to a containment
    // check: an empty needle is inside every haystack, which as an exclusion hides every item.
    return false;
  }

  if (spaced.includes(toSpacedWords(term, stem))) {
    return true;
  }

  return allowsSquashedMatch(term) && squashed.includes(termSquashed);
}

/**
 Splits a job's raw search term into include/exclude terms. Quoted phrases and bare words both
 support a leading `-` for exclusion. Terms are lowercased and deduped; terms with no matchable
 content are dropped. A term typed as both (`barbie -barbie`) keeps only its exclusion - the same
 precedence `matchesSearchQuery` already gives exclude over include - rather than surfacing it
 in both sets, which would double-count it and collide as a duplicate key wherever a caller
 renders one badge/row per term.
 */
export function parseSearchQuery(searchTerm: string): SearchQuery {
  const include = new Set<string>();
  const exclude = new Set<string>();

  for (const match of searchTerm.matchAll(TOKEN_PATTERN)) {
    const quoted = match[2];
    let negated = match[1] === '-';
    let term = quoted ?? match[3] ?? '';
    if (quoted === undefined && term.startsWith('-')) {
      negated = true;
      term = term.slice(1);
    }

    term = term.trim().toLowerCase();
    // Dropped here as well as guarded in termMatches: a term of pure punctuation would
    // otherwise reach the containment checks as an empty needle and match every item.
    if (!term || !toSquashed(term)) {
      continue;
    }

    (negated ? exclude : include).add(term);
  }

  for (const term of exclude) {
    include.delete(term);
  }

  return {include: [...include], exclude: [...exclude]};
}

/**
 The text an item is matched against. Mirrors the fields the server's own exclusion filter covers -
 title, both author fields, and every metadata field's content - so an item can't be hidden by one
 side and left visible by the other. Metadata carries `Description`/`Tags` plus `Keywords`/`Links`,
 and the server folds all of them into its tsvector.

 Building and stemming this is O(tokens) per item and descriptions run to kilobytes, so a caller
 filtering items should use `matchesItemSearchQuery`, which does that once per item object rather
 than once per pass.
 */
export function itemSearchText(
  item: Pick<Item, 'title' | 'author_handle' | 'author_name' | 'metadata'>
): string {
  return [
    item.title ?? '',
    item.author_handle ?? '',
    item.author_name ?? '',
    ...getMetaFields(item.metadata).map((field) => String(field.Content ?? ''))
  ].join(' ');
}

/**
 Which specific include/exclude terms are literally present in `text`, using the same matching
 rules as `matchesSearchQuery` (word-boundary + squashed containment, optionally stemmed). Unlike
 `matchesSearchQuery`, this reports per-term results rather than collapsing to one boolean, so a
 caller can show *why* an item matched rather than just whether it did.
 */
export function getTermMatches(
  text: string,
  query: SearchQuery,
  stem: boolean
): {matchedInclude: string[]; matchedExclude: string[]} {
  const spaced = toSpacedWords(text, stem);
  const squashed = toSquashed(text);

  return {
    matchedInclude: query.include.filter((term) => termMatches(spaced, squashed, term, stem)),
    matchedExclude: query.exclude.filter((term) => termMatches(spaced, squashed, term, stem))
  };
}

export function matchesSearchQuery(
  text: string,
  query: SearchQuery,
  options: MatchOptions
): boolean {
  return matchesHaystack(toSpacedWords(text, options.stem), toSquashed(text), query, options);
}

type CachedMatch = {query: SearchQuery; options: MatchOptions; matches: boolean};

// Keyed by the item object itself: item state is updated immutably, so a refetched or edited item
// is a new key, and a dropped one is collected along with its entry. Only the verdict is kept, not
// the folded text, which would roughly double what a large job already holds. Without this, the
// investigations workspace re-folded and re-stemmed every loaded item's full metadata text on every
// pass. On a grouped job of tens of thousands of items, the channel-aggregate poll alone meant a
// pass every 3 seconds, enough to pin the main thread and exhaust the heap (GT-16811).
const itemMatches = new WeakMap<object, CachedMatch>();

/**
 `matchesSearchQuery` over `itemSearchText(item)`, remembered per item for as long as the caller
 passes the same `query` object and equal options. Callers should memoize the query so a re-render
 isn't a cache miss.
 */
export function matchesItemSearchQuery(
  item: Pick<Item, 'title' | 'author_handle' | 'author_name' | 'metadata'>,
  query: SearchQuery,
  options: MatchOptions
): boolean {
  const cached = itemMatches.get(item);
  if (
    cached?.query === query &&
    cached.options.includeMode === options.includeMode &&
    cached.options.applyIncludes === options.applyIncludes &&
    cached.options.stem === options.stem
  ) {
    return cached.matches;
  }

  const matches = matchesSearchQuery(itemSearchText(item), query, options);
  itemMatches.set(item, {query, options, matches});
  return matches;
}

function matchesHaystack(
  spaced: string,
  squashed: string,
  query: SearchQuery,
  options: MatchOptions
): boolean {
  const {includeMode, applyIncludes, stem} = options;
  if (query.exclude.some((term) => termMatches(spaced, squashed, term, stem))) {
    return false;
  }

  if (!applyIncludes || query.include.length === 0) {
    return true;
  }

  return includeMode === 'all'
    ? query.include.every((term) => termMatches(spaced, squashed, term, stem))
    : query.include.some((term) => termMatches(spaced, squashed, term, stem));
}
